import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { PublicLayout } from "@/components/PublicLayout";
import { BrandIcon, TAGLINE } from "@/components/Brand";
import { api } from "@/lib/api";
import { Apple, Smartphone, Download as DownloadIcon, Loader2, Check, Mail, Crown } from "lucide-react";

const APP_STORE_URL = "#"; // Coming soon — swap with live App Store listing
const PLAY_STORE_URL = "#"; // Coming soon — swap with live Google Play listing

export default function WelcomeMember() {
  const location = useLocation();
  const [state, setState] = useState("loading"); // loading | confirmed | pending | none
  const [info, setInfo] = useState({ tier: null, email: null });

  useEffect(() => {
    const sid = new URLSearchParams(location.search).get("session_id");
    if (!sid) { setState("none"); return; }
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { if (!cancelled) { setInfo({ tier: data.tier, email: data.email }); setState("confirmed"); } return; }
        if (data.payment_status === "failed" || data.payment_status === "expired") { if (!cancelled) setState("pending"); return; }
      } catch {
        /* keep polling */
      }
      if (attempts >= 10) { if (!cancelled) setState("pending"); return; }
      setTimeout(() => { if (!cancelled) poll(); }, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [location.search]);

  const confirmed = state === "confirmed" || state === "none";

  return (
    <PublicLayout>
      <section className="relative overflow-hidden spark-grid-bg min-h-[70vh] flex items-center">
        <div
          className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[640px] h-[360px] rounded-full spark-glow-pulse"
          style={{ background: "radial-gradient(circle, rgba(225,29,72,0.2), transparent 70%)" }}
        />
        <div className="relative w-full max-w-xl mx-auto px-5 sm:px-8 py-20 text-center" data-testid="welcome-member">
          {state === "loading" && (
            <div className="flex flex-col items-center gap-4 text-slate-300" data-testid="welcome-loading">
              <Loader2 className="w-8 h-8 animate-spin text-[#E11D48]" />
              <p>Confirming your membership…</p>
            </div>
          )}

          {confirmed && (
            <>
              {/* Animated flame emblem with gold check badge */}
              <div className="relative mx-auto w-24 h-24">
                <div className="absolute inset-0 rounded-full spark-glow-pulse" style={{ background: "radial-gradient(circle, rgba(245,158,11,0.35), transparent 70%)" }} />
                <div className="relative flex items-center justify-center w-24 h-24 rounded-3xl spark-gradient shadow-[0_0_40px_rgba(225,29,72,0.5)]">
                  <BrandIcon className="h-12 w-auto brightness-0 invert" />
                </div>
                <span className="absolute -bottom-1 -right-1 flex items-center justify-center w-9 h-9 rounded-full bg-[#F59E0B] border-4 border-[#0F172A]">
                  <Check className="w-4 h-4 text-[#0F172A]" strokeWidth={3} />
                </span>
              </div>

              <h1 className="mt-8 font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-white" data-testid="welcome-title">
                Welcome to the Inner Circle!
              </h1>
              <p className="mt-3 text-sm text-slate-500">{TAGLINE}</p>

              {/* Status box */}
              <div className="mt-8 rounded-2xl spark-card p-5 text-left flex items-center gap-3" data-testid="welcome-status">
                <span className="flex items-center justify-center w-10 h-10 rounded-xl spark-gradient shrink-0">
                  <Check className="w-5 h-5 text-white" strokeWidth={3} />
                </span>
                <div>
                  <p className="font-heading font-bold text-white">Membership Confirmed</p>
                  <p className="text-sm text-slate-400">Your subscription perks are ready to unlock.</p>
                </div>
              </div>

              {/* 2-step instructions */}
              <div className="mt-6 space-y-3 text-left">
                <div className="flex items-start gap-3 rounded-2xl spark-card p-4" data-testid="welcome-step-1">
                  <span className="flex items-center justify-center w-7 h-7 rounded-full spark-gradient text-white text-sm font-bold shrink-0">1</span>
                  <p className="text-sm text-slate-300"><span className="text-white font-semibold">Download Sparkd</span> on your phone using the store buttons below.</p>
                </div>
                <div className="flex items-start gap-3 rounded-2xl spark-card p-4" data-testid="welcome-step-2">
                  <span className="flex items-center justify-center w-7 h-7 rounded-full spark-gradient text-white text-sm font-bold shrink-0">2</span>
                  <p className="text-sm text-slate-300 flex items-start gap-1.5">
                    <Mail className="w-4 h-4 mt-0.5 text-[#F59E0B] shrink-0" />
                    <span>Sign in with <span className="text-white font-semibold">this exact email address</span> to automatically unlock your subscription perks.</span>
                  </p>
                </div>
              </div>

              {/* Download badges */}
              <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
                <a href={APP_STORE_URL} data-testid="welcome-ios" className="inline-flex items-center gap-3 px-6 py-3.5 rounded-2xl spark-gradient spark-gradient-hover text-white font-semibold transition-all active:scale-95">
                  <Apple className="w-6 h-6" />
                  <span className="text-left leading-tight"><span className="block text-[10px] opacity-80">Download on the</span>iOS App Store</span>
                </a>
                <a href={PLAY_STORE_URL} data-testid="welcome-android" className="inline-flex items-center gap-3 px-6 py-3.5 rounded-2xl border border-white/[0.12] bg-[#1F242D] text-white font-semibold hover:border-[#F43F5E]/40 transition-all">
                  <Smartphone className="w-6 h-6" />
                  <span className="text-left leading-tight"><span className="block text-[10px] text-slate-400">Get it on</span>Google Play</span>
                </a>
              </div>
              <Link to="/download" className="mt-6 inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-[#F43F5E]" data-testid="welcome-download-link">
                <DownloadIcon className="w-4 h-4" /> All download options
              </Link>
            </>
          )}

          {state === "pending" && (
            <>
              <div className="mx-auto flex items-center justify-center w-16 h-16 rounded-2xl bg-[#1F242D] border border-white/[0.08]">
                <Loader2 className="w-8 h-8 text-[#F59E0B]" />
              </div>
              <h1 className="mt-6 font-heading text-3xl sm:text-4xl font-extrabold text-white">Almost there</h1>
              <p className="mt-4 text-slate-300">
                Your payment is still processing. You'll get a confirmation email shortly — then head to the download page.
              </p>
              <Link to="/download" className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-2xl spark-gradient text-white font-semibold" data-testid="welcome-pending-download">
                <DownloadIcon className="w-5 h-5" /> Download Sparkd
              </Link>
            </>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
